import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/dist/client/router';
import { Error as ErrorIcon } from '@styled-icons/material';
import isHotkey from 'is-hotkey';
import { ExternalLink } from '@styled-icons/feather';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { parse } from 'tldts';
import styles from '../styles/Duplicate.module.css';

import {
  DEV,
  DUPLICATE_PAGE_STORAGE_KEY, LOGO_NAME, RENDERER_DOMAIN, OLD_PATH_RESOLUTION_DOMAIN,
} from '../constants';
import DuplicateNavbar from '../components/DuplicateNavbar';
import { IPage, ISpace, ISpaceRoute } from '../types';
import { errToString } from '../utils/errors';
import getPageIDFromPath from '../utils/getPageIDFromPath';
import PopupNotice from '../sharedComponents/PopupNotice';
import Loading from '../components/Loading';
import { useDuplicatePageMutation } from '../store';
import SpacePageSEO from '../head/SpacePageSeo';
import { api } from '../data/api';

function Duplicate() {
  const router = useRouter();
  const { url, auth_method: authMethod } = router.query;

  const urlToSearchVal = (rawURL: string) => {
    const decodedURL = decodeURIComponent(rawURL as string);
    return decodedURL.replace(`https://${RENDERER_DOMAIN}/`, '').replace(`http://${RENDERER_DOMAIN}/`, '');
  };

  const [loading, setloading] = useState(false);
  const [err, seterr] = useState(null);
  const [space, setspace] = useState(null);
  const [searchVal, setsearchVal] = useState(
    (url && urlToSearchVal(url as string)) || '',
  );
  const [iframeSrc, setiframeSrc] = useState(url && decodeURIComponent(url as string));
  const [slug, setslug] = useState(null);
  const [path, setpath] = useState(null);
  const [page, setpage] = useState(null);

  const {
    mutate: duplicatePage,
    isLoading: loadingDuplicatePage,
  } = useDuplicatePageMutation();

  const onKeyDown = (e) => {
    if (isHotkey('enter')(e)) {
      setpage(null);
      seterr(null);
      setiframeSrc(searchVal);
      router.push({ pathname: '/duplicate', query: { url: searchVal } });
    }
  };

  const getSpace = async (spaceSlug: string): Promise<ISpace> => {
    try {
      const resp = await api().space().getPublic(spaceSlug, 'SLUG');
      const respSpace = resp?.data as ISpace;
      setspace(respSpace);
      return respSpace;
    } catch (e) {
      seterr(errToString(e));
      setloading(false);
    }
    return null;
  };
  const getPage = async (_space: ISpace, pagePath: string[]) => {
    try {
      const routes = _space?.routes as ISpaceRoute;
      const pageID = getPageIDFromPath(routes, pagePath as string[]);
      if (!pageID) {
        seterr(`Can't find page ${iframeSrc}`);
        setloading(false);
        return;
      }
      const resp = await api().page().getPublished(pageID);
      setpage(resp?.data as ISpace);
      if (!resp?.data?.allow_duplicate) {
        setloading(false);
      }
    } catch (e) {
      seterr(errToString(e));
      setloading(false);
    }
  };
  const getSpaceAndPage = async (pathURL: string) => {
    setloading(true);
    const cleanedUrl = (pathURL as string).replace('https://', '').replace('http://', '');
    const urlArr = cleanedUrl.split('/');
    const { hostname } = parse(urlArr[0] || '');
    const isLinkTDSubdomain = hostname?.endsWith(RENDERER_DOMAIN);

    if (
      !urlArr
      || urlArr.length <= 0
      || !(isLinkTDSubdomain || urlArr[0] === OLD_PATH_RESOLUTION_DOMAIN)) {
      seterr(`URL is invalid. Your site URL should look like 'template.${RENDERER_DOMAIN}'.`);
      setloading(false);
      return;
    }
    let spaceSlug: string;
    if (isLinkTDSubdomain) {
      spaceSlug = urlArr.shift();
      spaceSlug = spaceSlug?.replace(`.${RENDERER_DOMAIN}`, '');
    } else {
      urlArr.shift();
      spaceSlug = urlArr.shift();
    }

    setslug(spaceSlug);
    setpath(urlArr);
    const sp = await getSpace(spaceSlug);
    if (!sp) {
      seterr(`Can't find page ${iframeSrc}`);
      setloading(false);
      return;
    }
    await getPage(sp, urlArr);
  };

  useEffect(() => {
    if (url) {
      let decodedURL = decodeURIComponent(url as string);
      if (!decodedURL.startsWith('http://') && !decodedURL.startsWith('https://')) {
        decodedURL = `http${!DEV ? 's' : ''}://${decodedURL}`;
      }
      setsearchVal(urlToSearchVal(url as string));
      setiframeSrc(decodedURL);
      if (localStorage) {
        localStorage.setItem(DUPLICATE_PAGE_STORAGE_KEY, url as string);
      }
    }
  }, [url]);

  useEffect(() => {
    if (authMethod && localStorage && localStorage.getItem(DUPLICATE_PAGE_STORAGE_KEY)) {
      router.push({ pathname: '/duplicate', query: { url: localStorage.getItem(DUPLICATE_PAGE_STORAGE_KEY) } });
    }
  }, [authMethod]);

  useEffect(() => {
    if (iframeSrc) {
      try {
        let iSrc = iframeSrc;
        if (!iSrc.startsWith('http') && !iSrc.startsWith('https')) {
          iSrc = `https://${iSrc}`;
        }
        const iframeURL = new URL(iSrc);

        if (!iframeURL.host.endsWith(RENDERER_DOMAIN)
          && iframeURL.host !== OLD_PATH_RESOLUTION_DOMAIN) {
          throw Error(`Please use a Typedream URL, for example 'template.${RENDERER_DOMAIN}`);
        }
        getSpaceAndPage(iSrc as string);
      } catch (e) {
        seterr(String(e));
      }
    }
  }, [iframeSrc]);

  const onDuplicate = async (create: boolean, destinationSpaceID?: string) => {
    setloading(true);
    try {
      const destSpace = await duplicatePage({
        create, destinationSpaceID, slug, pagePath: path, duplicateOwnPage: false,
      }).unwrap();

      if (destSpace) {
        const destSpaceRoute = destSpace.routes as ISpaceRoute;
        const redirectURL = `/s/${destSpace.slug}/${destSpace.id}/${destSpaceRoute?.page_id}`;
        router.push(redirectURL);
      } else {
        setloading(false);
      }
    } catch (error) {
      toast.error(errToString(error));
      setloading(false);
    }
  };

  return (
    <div className={styles.container}>
      <SpacePageSEO space={space as ISpace} page={page as IPage} />
      <PopupNotice
        bottom
        right
        className="with-text-color"
        icon={<ErrorIcon size={20} className="with-danger-text-color" />}
        open={!!err}
        onClose={() => {
          seterr(null);
        }}
      >
        {err}
      </PopupNotice>
      <DuplicateNavbar
        spaceName={space?.name}
        pageName={page?.name}
        pageID={page?.id}
        onDuplicate={!loading && !err && page?.allow_duplicate
          ? onDuplicate
          : () => { /* do nothing */ }}
        disabled={!path || err || !page?.allow_duplicate}
      />
      <div className={styles.main}>
        <div className={styles.iframeContainer}>
          <div className={styles.urlBarContainer}>
            <div className={styles.dotsContainer}>
              <div />
              <div />
              <div />
            </div>
            <div className={styles.urlInput}>
              <div className={styles.urlInputBackground}>
                <div className={styles.inputPrefix}>
                  <input
                    type="text"
                    value={searchVal}
                    onChange={(e) => setsearchVal(e.target.value)}
                    className={styles.input}
                    onKeyDown={onKeyDown}
                    placeholder={`e.g. ${LOGO_NAME.toLowerCase()}`}
                  />
                </div>

                <a href={iframeSrc} target="_blank" rel="noreferrer">
                  <ExternalLink size={15} />
                </a>
                {!searchVal && (
                  <div className={classNames(styles.floatyTooltip, 'box-no-border', 'is-size-7', 'fade-in-from-bottom', 'with-lighter-text-color')}>
                    Enter a
                    {' '}
                    <span className="with-text-color">Typedream URL</span>
                    {' '}
                    above, like
                    {' '}
                    {`template.${RENDERER_DOMAIN}`}
                  </div>
                )}
                {searchVal && page && !page?.allow_duplicate && (
                  <div className={classNames(styles.floatyTooltip, 'box-no-border', 'is-size-7', 'fade-in-from-bottom', 'with-lighter-text-color')}>
                    This page cannot be duplicated
                  </div>
                )}
              </div>
            </div>
            <div />
          </div>

          {page?.allow_duplicate ? (
            <iframe
              title="Preview"
              className={styles.iframe}
              src={iframeSrc}
              onError={() => setloading(false)}
              onLoad={() => setloading(false)}
            />
          ) : <div className={styles.iframe} />}

        </div>
      </div>
      <Loading loading={loading || loadingDuplicatePage} />
    </div>
  );
}

Duplicate.theme = 'light';
export default Duplicate;
